import { useState } from "react";
import { lsLoad, lsSave } from "../../tools/Storage.ts";
import StorageEnum from "../../enums/Storage.enum.ts";
import {
  FileTargets,
  type MarketMarketIO,
} from "../../generated/fresh-guard-api";
import { useMarketUpdateOptimistic } from "../../apis/market/useMarketUpdateOptimistic.ts";
import { toast } from "react-toastify";
import { toastOptions } from "../../tools/helpers/ToastHelper.ts";
import { reloadPage } from "../../tools/Routing.ts";
import { useFileUpdate } from "../../apis/file/useFileUpdate.ts";
import { extractMessageFromApi } from "../../tools/helpers/MessageHelper.ts";
import { useTranslation } from "react-i18next";

export const useMarketPropertiesDialogController = () => {
  const { t } = useTranslation();
  const market = lsLoad(StorageEnum.Market) as MarketMarketIO;
  const [name, setName] = useState(market.name ?? "");
  const [street, setStreet] = useState(market.address?.street ?? "");
  const [houseNumber, setHouseNumber] = useState(
    market.address?.houseNumber ?? "",
  );
  const [zip, setZip] = useState(market.address?.zip ?? 0);
  const [city, setCity] = useState(market.address?.city ?? "");
  const [district, setDistrict] = useState(market.address?.district ?? "");
  const [areaX, setAreaX] = useState(market.configuration?.area?.width ?? 0);
  const [areaY, setAreaY] = useState(market.configuration?.area?.height ?? 0);
  const [filename, setFilename] = useState(
    market.configuration?.originalFilenameMarketDrawing,
  );
  const [enableMarketDrawing, setEnableMarketDrawing] = useState(
    market.configuration?.enableMarketDrawing ?? false,
  );
  const [marketMarketIO, setMarketMarketIO] = useState<MarketMarketIO>({});

  const { mutate: mutateMarketUpdate } = useMarketUpdateOptimistic();
  const { mutate: mutateFileUpdate } = useFileUpdate();

  const handleZipChange = (value: string) => {
    if (!isNaN(parseInt(value))) {
      setZip(parseInt(value));
    }
  };

  const handleAreaXChange = (value: string) => {
    if (!isNaN(parseInt(value))) {
      setAreaX(parseInt(value));
    }
  };

  const handleAreaYChange = (value: string) => {
    if (!isNaN(parseInt(value))) {
      setAreaY(parseInt(value));
    }
  };

  const handleToggleClicked = () => {
    setEnableMarketDrawing(!enableMarketDrawing);
  };

  const handleSaveClick = () => {
    setMarketMarketIO({
      id: market.id,
      name: name,
      configuration: {
        area: {
          height: areaY,
          width: areaX,
        },
        enableMarketDrawing: enableMarketDrawing,
        filenameMarketDrawing: "",
      },
      address: {
        street: street,
        houseNumber: houseNumber,
        zip: zip,
        city: city,
        district: district,
      },
    });

    if (marketMarketIO.name?.trim()) {
      mutateMarketUpdate(
        { marketMarketIO: marketMarketIO },
        {
          onSuccess: (market) => {
            lsSave(StorageEnum.Market, market);
            reloadPage();
          },
        },
      );
    }
  };

  const handleUploadClick = (files: FileList | null) => {
    if (!files || !files.length) {
      return;
    }

    const pickedFile = files[0];
    const objectUrl = URL.createObjectURL(pickedFile);
    const img = new Image();
    img.src = objectUrl;

    img.onload = () => {
      setAreaX(img.width);
      setAreaY(img.height);
    };

    setFilename(pickedFile.name);

    if (market.id) {
      mutateFileUpdate(
        {
          entityId: market.id?.toString(),
          target: FileTargets.MarketDrawing,
          file: pickedFile,
        },
        {
          onSuccess: (file) => {
            toast.success(extractMessageFromApi(file, t).message, toastOptions);
          },
        },
      );
    }
  };

  return {
    name,
    street,
    houseNumber,
    zip,
    city,
    district,
    areaX,
    areaY,
    enableMarketDrawing,
    filename,
    setName,
    setStreet,
    setHouseNumber,
    handleZipChange,
    setCity,
    setDistrict,
    handleAreaXChange,
    handleAreaYChange,
    handleToggleClicked,
    handleSaveClick,
    handleUploadClick,
  };
};
